import {
  CreateOutOfStockProductRequest,
  CreateProductMenuDto,
  DosingDetailsDto,
  DosingUpdateDto,
  IngredientCategoriesDto,
  IngredientDto,
  MutationResponseDto,
  NewProductCategoryDto,
  NewProductSizeDto,
  OutOfStockProductDto,
  ProductCategoryDetailsDto,
  ProductDto,
  ProductMenuDto,
  ProductMenuOperationType,
  ProductPriceDto,
  ProductSizeDto,
  ProductsApiClient,
  S4DIngredientDto,
  S4DMenuDto,
  S4DProductDto,
  ShopProductMenuDto,
  UserDetailsDTO,
  WarehouseItemDto,
  WarehouseItemCategoryDto,
  UpcomingProductDto,
  OutOfStockIngredientDto,
  CreateOutOfStockIngredientRequest,
  WarehouseItemUnitOverrideDto,
  VatDto,
  TagDto,
  DisplayGroupDto,
  UpdateProductDisplayOrderDto,
  UpdateOutOfStockProductRequest,
  UpdateOutOfStockIngredientRequest,
  GetProductPriceGroupDto,
  UpsertProductPriceGroupDto,
  ImportFoodoraCatalogToShopsRequest,
  GetAllOutOfStockItemsResponse,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { getConfiguration } from '../shared/utils/configuration';
import Axios from 'axios';
import { getRequestHeaders } from './utils';
import { isNotFoundError } from '../shared/utils/apiErrors';
import {
  MutationFunction,
  UseQueryResult,
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getProductsApiError } from './products-api.errors';
import { ApiMutationOptions, ApiMutationUiOptions, useApiMutation } from './useApiMutation';
import { t } from 'i18next';

let client: ProductsApiClient | null = null;

const getApiClient = (): ProductsApiClient => {
  if (client) return client;

  const baseUrl = getConfiguration<string>('productsService.root', '');

  // products-api-client 5.6.0 stopped JSON.parse-ing response bodies in its generated
  // processXxx methods and now expects axios to have parsed the JSON already, so this
  // instance must keep axios's default parsing (no `transformResponse: []`).
  const instance = Axios.create({
    timeout: 10000,
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  client = new ProductsApiClient(baseUrl, instance);
  return client;
};

/**
 * Products
 */

export const getAllProducts = async (includeArchived = true): Promise<ProductDto[]> => {
  const client = getApiClient();
  const allProducts = await client.getAllProducts();
  if (!includeArchived) {
    return allProducts.filter((product) => !product.isArchived);
  }
  return allProducts.map((product) => {
    if (product.isArchived) {
      return { ...product, name: `${product.name} [ARKISTOITU]` };
    }
    return product;
  });
};

const getAllS4DProducts = (): Promise<S4DProductDto[]> => {
  const client = getApiClient();
  return client.getS4DProducts();
};

export const getSingleProduct = async (productId: number): Promise<ProductDto> => {
  const client = getApiClient();
  const product = await client.getProductById(String(productId));
  if (product.isArchived) {
    return { ...product, name: `${product.name} [ARKISTOITU]` };
  }
  return product;
};

const getCategorySizes = (productCategoryId: number): Promise<ProductSizeDto[]> => {
  const client = getApiClient();
  return client.getCategorySizes(productCategoryId);
};

const getProductSizes = async (productId: number): Promise<ProductSizeDto[]> => {
  if (productId === 0) return [];

  const client = getApiClient();
  const sizes = await client.getProductSizes(productId);
  return sizes;
};

const getAllProductSizes = (): Promise<ProductSizeDto[]> => {
  const client = getApiClient();
  try {
    return client.getAllProductSizes();
  } catch (e) {
    console.error(e);
    return Promise.resolve([]);
  }
};

export const getProductPrices = (productId: number): Promise<ProductPriceDto[]> => {
  const client = getApiClient();
  return client.getProductPrices(productId);
};

const getAllProductPrices = (): Promise<ProductPriceDto[]> => {
  const client = getApiClient();
  return client.getAllProductPrices();
};

export const getAllOutOfStockProducts = (): Promise<OutOfStockProductDto[]> => {
  const client = getApiClient();
  return client.getOutOfStockProducts();
};

export const getAllOutOfStockIngredients = (): Promise<OutOfStockIngredientDto[]> => {
  const client = getApiClient();
  return client.getOutOfStockIngredients();
};

export const getAllOutOfStockItemsByShopExternalId = (
  shopExternalId: string
): Promise<GetAllOutOfStockItemsResponse> => {
  const client = getApiClient();
  return client.getOutOfStockItemsByShopExternalId(shopExternalId);
};

const isOutOfStockIngredientRequest = (
  request: CreateOutOfStockProductRequest | CreateOutOfStockIngredientRequest
): request is CreateOutOfStockIngredientRequest => {
  return 'ingredientId' in request;
};

export const addOutOfStockItem = (
  request: CreateOutOfStockProductRequest | CreateOutOfStockIngredientRequest
): Promise<void> => {
  const client = getApiClient();
  if (isOutOfStockIngredientRequest(request)) {
    return client.createOutOfStockIngredient(request);
  }
  return client.createOutOfStockProduct(request);
};

export const updateOutOfStockItem = (
  request: UpdateOutOfStockProductRequest | UpdateOutOfStockIngredientRequest
): Promise<void> => {
  const client = getApiClient();
  if (isOutOfStockIngredientRequest(request)) {
    return client.updateOutOfStockIngredient(request);
  }
  return client.updateOutOfStockProduct(request);
};

export const removeOutOfStock = async (
  request: CreateOutOfStockProductRequest | CreateOutOfStockIngredientRequest
): Promise<void> => {
  const client = getApiClient();
  try {
    if (isOutOfStockIngredientRequest(request)) {
      const { ingredientId, externalShopId } = request;
      await client.deleteOutOfStockIngredient(ingredientId, externalShopId);
      return;
    }
    const { productId, externalShopId } = request;
    await client.deleteOutOfStockProduct(productId, externalShopId);
  } catch (error) {
    // The API answers 404 when the marking is already gone (it expired, or someone else restored the
    // item). That is the state the caller asked for, so the delete is idempotent: resolve. Anything
    // else (401, 5xx, network) is a real failure and must still reach the caller.
    if (!isNotFoundError(error)) throw error;
  }
};

export const updateProductPrice = (productId: number, prices: ProductPriceDto[]): Promise<MutationResponseDto> => {
  const client = getApiClient();
  return client.updateProductPrice(productId, prices);
};

export const upsertProduct = (product: ProductDto): Promise<MutationResponseDto> => {
  const client = getApiClient();
  if (!product.productId) {
    return client.createProduct(product);
  }
  return client.updateProduct(product.productId, product);
};

type AddSizePayload = {
  categoryId: number;
  size: NewProductSizeDto;
};

export const addProductSize = (payload: AddSizePayload): Promise<void> => {
  const client = getApiClient();
  return client.addNewProductSize(payload.categoryId, payload.size);
};

export const addProductCategory = (category: NewProductCategoryDto): Promise<void> => {
  const client = getApiClient();
  return client.addNewProductCategory(category);
};

const getAllProductCategories = (): Promise<ProductCategoryDetailsDto[]> => {
  const client = getApiClient();
  return client.getAllProductCategoriesWithDetails();
};

const getAllUpcomingProducts = (): Promise<UpcomingProductDto[]> => {
  const client = getApiClient();
  return client.getUpcomingProducts();
};

const getProductPriceGroups = (productId: number): Promise<GetProductPriceGroupDto[]> => {
  const client = getApiClient();
  return client.getProductPricesGroups(productId);
};

export const upsertProductPriceGroups = (
  productId: number,
  priceGroups: UpsertProductPriceGroupDto[]
): Promise<MutationResponseDto> => {
  const client = getApiClient();
  return client.updateProductPriceGroups(productId, priceGroups);
};

/**
 * Ingredients
 */

const getSingleIngredient = (ingredientId: number): Promise<IngredientDto> => {
  const client = getApiClient();
  return client.getIngredientById(ingredientId);
};

export const upsertIngredient = (ingredientData: IngredientDto): Promise<MutationResponseDto> => {
  const client = getApiClient();
  if (!ingredientData.ingredientId) {
    return client.createIngredient(ingredientData);
  }
  return client.updateIngredient(ingredientData.ingredientId, ingredientData);
};

const getIngredientCategories = (): Promise<IngredientCategoriesDto> => {
  const client = getApiClient();
  return client.getIngredientCategories();
};

const getAllIngredients = async (includeArchived = true): Promise<IngredientDto[]> => {
  const client = getApiClient();
  const ingredients = await client.getAllIngredientsWithInactive();
  if (!includeArchived) {
    return ingredients.filter((ingredient) => !ingredient.isArchived);
  }
  return ingredients.map((ingredient) => {
    if (ingredient.isArchived) {
      return { ...ingredient, name: `${ingredient.name} [ARKISTOITU]` };
    }
    return ingredient;
  });
};

const getAllS4DIngredients = (): Promise<S4DIngredientDto[]> => {
  const client = getApiClient();
  return client.getS4DIngredients();
};

const getS4DMenusForStore = (externalStoreId: string): Promise<S4DMenuDto[]> => {
  const client = getApiClient();
  return client.getS4DMenus(externalStoreId);
};

/**
 * Dosings
 */

export const updateIngredientDosings = (id: number, payload: DosingUpdateDto): Promise<void> => {
  const client = getApiClient();
  return client.updateIngredientDosings(id, payload);
};

const getAllDosings = (): Promise<DosingDetailsDto> => {
  const client = getApiClient();
  return client.getAllDosings();
};

/**
 * Warehouse
 */

const getSingleWarehouseItem = (itemId: number | string): Promise<WarehouseItemDto> => {
  const client = getApiClient();
  return client.getWarehouseItem(String(itemId));
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getSingleWarehouseItemUnitOverride = (itemId: number | string): Promise<WarehouseItemUnitOverrideDto> => {
  const client = getApiClient();
  return client.getWarehouseItemUnitOverrideOfWarehouseItem(String(itemId));
};

const getAllWarehouseItems = (): Promise<WarehouseItemDto[]> => {
  const client = getApiClient();
  return client.getWarehouseItems();
};

const getAllWarehouseItemCategories = (): Promise<WarehouseItemCategoryDto[]> => {
  const client = getApiClient();
  return client.getWarehouseItemCategories();
};

export const setWarehouseItemCategory = (itemId: string, itemCategoryId: number): Promise<void> => {
  const client = getApiClient();
  return client.setWarehouseItemCategory(itemId, itemCategoryId);
};

export const updateWarehouseItemUnitOverride = (
  itemId: string,
  unitOverride: WarehouseItemUnitOverrideDto
): Promise<void> => {
  const client = getApiClient();
  return client.updateWarehouseItemUnitOverride(itemId, unitOverride);
};

/**
 * Product menus
 */

export const upsertProductMenu = async (
  productMenu: ProductMenuDto | CreateProductMenuDto
): Promise<MutationResponseDto> => {
  const client = getApiClient();

  const isCreateRequest = !('productMenuId' in productMenu);
  if (isCreateRequest) {
    return client.createProductMenu(productMenu);
  }
  await client.updateProductMenu(productMenu);
  return {
    id: 0,
  };
};

const getAllProductMenus = (): Promise<ProductMenuDto[]> => {
  const client = getApiClient();
  return client.getAllProductMenus();
};

export const getSingleProductMenu = (id: number): Promise<ProductMenuDto> => {
  const client = getApiClient();
  return client.getProductMenu(id);
};

export const getDefaultProductMenu = (): Promise<ProductMenuDto> => {
  const client = getApiClient();
  return client.getDefaultProductMenu();
};

export const getConciseProductMenu = (): Promise<ProductMenuDto> => {
  const client = getApiClient();
  return client.getConciseProductMenu();
};

export const getFoodoraProductMenu = (): Promise<ProductMenuDto> => {
  const client = getApiClient();
  return client.getFoodoraProductMenu();
};

export const getProductMenusForShop = (shopExternalId: string): Promise<ShopProductMenuDto[]> => {
  const client = getApiClient();
  return client.getShopProductMenusForShop(shopExternalId);
};

export const isProductInDefaultMenu = async (productId: number, concise = false): Promise<boolean> => {
  try {
    const menu = await (concise ? getConciseProductMenu() : getDefaultProductMenu());
    if (!menu?.productIds) {
      throw new Error('Default menu products not found!');
    }

    return menu.productIds.some((pId) => pId === productId);
  } catch (err) {
    console.error(err);
    return false;
  }
};

export const addProductToDefaultMenu = async (productId: number, concise = false): Promise<MutationResponseDto> => {
  const menu = await (concise ? getConciseProductMenu() : getDefaultProductMenu());

  const client = getApiClient();
  return client.updateProductToMenu({
    productId,
    productMenuId: menu.productMenuId,
    operation: ProductMenuOperationType.Add,
  });
};

export const removeProductFromDefaultMenu = async (
  productId: number,
  concise = false
): Promise<MutationResponseDto> => {
  const menu = await (concise ? getConciseProductMenu() : getDefaultProductMenu());

  const client = getApiClient();
  return client.updateProductToMenu({
    productId,
    productMenuId: menu.productMenuId,
    operation: ProductMenuOperationType.Remove,
  });
};

export const updateIngredientMenuMembership = (
  ingredientId: number,
  productMenuId: number,
  shouldBeInMenu: boolean
): Promise<MutationResponseDto> => {
  const client = getApiClient();
  return client.updateIngredientToMenu({
    ingredientId,
    productMenuId,
    operation: shouldBeInMenu ? ProductMenuOperationType.Add : ProductMenuOperationType.Remove,
  });
};

/**
 * User
 */

export const getUserDetails = (): Promise<UserDetailsDTO> => {
  const client = getApiClient();
  return client.getUserDetails();
};

/**
 * Vat
 */

export const getAllVatRates = (): Promise<VatDto[]> => {
  const client = getApiClient();
  return client.getAllVatRates();
};

/**
 * Tags
 */

export const getAllTags = (): Promise<TagDto[]> => {
  const client = getApiClient();
  return client.getAllTags();
};

/**
 * Hooks
 */

type Options<T> = Parameters<typeof useQuery<T>>['2'];

const getDefaultOptions = <T>(): Options<T> => ({
  enabled: true,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
});

export const singleProductCacheKey = (id: number): [string, number] => ['product', id];

export const useProduct = (
  id: number,
  options = getDefaultOptions<ProductDto>()
): UseQueryResult<ProductDto, unknown> =>
  useQuery<ProductDto>(singleProductCacheKey(id), () => getSingleProduct(id), options);

export const useProductCategorySizes = (productCategoryId: number): UseQueryResult<ProductSizeDto[], unknown> => {
  return useQuery(['product-category-sizes', productCategoryId], () => getCategorySizes(productCategoryId));
};

export const useProductSizes = (
  productId: number,
  options = getDefaultOptions<ProductSizeDto[]>()
): UseQueryResult<ProductSizeDto[], unknown> => {
  return useQuery(['product-sizes', productId], () => getProductSizes(productId), options);
};

export interface ProductSize extends ProductSizeDto {
  productCategoryName: string;
  combinedName: string;
}

export const useAllProductSizes = (): UseQueryResult<ProductSize[], unknown> => {
  const { data: productCategories } = useAllProductCategories();

  return useQuery<ProductSize[]>(
    ['product-sizes-all'],
    async () => {
      const productSizes = await getAllProductSizes();

      return productSizes.map((size) => {
        const categoryName =
          productCategories?.find((category) => category.productCategoryId === size.productCategoryId)
            ?.productCategoryName || '';
        return {
          ...size,
          productCategoryName: categoryName,
          combinedName: `${categoryName}: ${size.name}`,
        };
      });
    },
    { enabled: Boolean(productCategories) }
  );
};

export const useProductPrices = (productId: number): UseQueryResult<ProductPriceDto[], unknown> => {
  return useQuery(['product-prices', productId], () => getProductPrices(productId));
};

export const allProductPricesCacheKey = ['product-prices-all'];

export const useAllProductPrices = (
  options = getDefaultOptions<ProductPriceDto[]>()
): UseQueryResult<ProductPriceDto[], unknown> => {
  return useQuery<ProductPriceDto[]>(allProductPricesCacheKey, getAllProductPrices, options);
};

export const useProductPricesForProductIds = (productIds: number[]): UseQueryResult<ProductPriceDto[], unknown>[] => {
  return useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ['product-prices', productId],
      queryFn: () => getProductPrices(productId),
    })),
  });
};

export const useProductPriceGroups = (productId: number, enabled = true): UseQueryResult<GetProductPriceGroupDto[]> => {
  return useQuery(['product-price-groups', productId], () => getProductPriceGroups(productId), { enabled });
};

export const useIngredient = (
  ingredientId: number,
  options = getDefaultOptions<IngredientDto>()
): UseQueryResult<IngredientDto, unknown> => {
  return useQuery(['ingredient', ingredientId], () => getSingleIngredient(ingredientId), options);
};

export const useIngredientCategories = (
  options = getDefaultOptions<IngredientCategoriesDto>()
): UseQueryResult<IngredientCategoriesDto, unknown> => {
  return useQuery(['ingredient-categories'], getIngredientCategories, options);
};

export const allIngredientsCacheKey = ['ingredients'];

export const useAllIngredients = (
  includeArchived = true,
  options = getDefaultOptions<IngredientDto[]>()
): UseQueryResult<IngredientDto[], unknown> => {
  return useQuery<IngredientDto[]>(allIngredientsCacheKey, () => getAllIngredients(includeArchived), options);
};

export const allDosingsCacheKey = ['dosings'];

export const useAllDosings = (
  options = getDefaultOptions<DosingDetailsDto>()
): UseQueryResult<DosingDetailsDto, unknown> => {
  return useQuery<DosingDetailsDto>(allDosingsCacheKey, getAllDosings, options);
};

export const allProductsCacheKey = ['products'];

export const useAllProducts = (
  includeArchived = true,
  options = getDefaultOptions<ProductDto[]>()
): UseQueryResult<ProductDto[], unknown> => {
  return useQuery<ProductDto[]>(allProductsCacheKey, () => getAllProducts(includeArchived), options);
};

export const upcomingProductsCacheKey = ['upcoming-products'];
// Prefix of the per-product default price calculations on the upcoming products page.
export const upcomingProductPricesCacheKey = ['upcoming-product-default-prices'];

export const useUpcomingProducts = (
  options = getDefaultOptions<UpcomingProductDto[]>()
): UseQueryResult<UpcomingProductDto[], unknown> =>
  useQuery<UpcomingProductDto[]>(upcomingProductsCacheKey, getAllUpcomingProducts, options);

export const useAllS4DProducts = (options?: Options<S4DProductDto[]>): UseQueryResult<S4DProductDto[], unknown> => {
  return useQuery<S4DProductDto[]>(['all-s4d-products'], getAllS4DProducts, options);
};

export const useAllS4DIngredients = (
  options?: Options<S4DIngredientDto[]>
): UseQueryResult<S4DIngredientDto[], unknown> => {
  return useQuery<S4DIngredientDto[]>(['all-s4d-ingredients'], getAllS4DIngredients, options);
};

export const useS4DMenusForStore = (externalStoreId: string): UseQueryResult<S4DMenuDto[], unknown> => {
  return useQuery<S4DMenuDto[]>(['s4d-menus', externalStoreId], () => getS4DMenusForStore(externalStoreId), {
    enabled: !!externalStoreId,
    retry: false,
  });
};

export const useWarehouseItem = (
  itemId: number | string,
  options = getDefaultOptions<WarehouseItemDto>()
): UseQueryResult<WarehouseItemDto, unknown> => {
  return useQuery<WarehouseItemDto>(['warehouse-item', itemId], () => getSingleWarehouseItem(itemId), options);
};

export const useWarehouseItems = (
  options = getDefaultOptions<WarehouseItemDto[]>()
): UseQueryResult<WarehouseItemDto[], unknown> =>
  useQuery<WarehouseItemDto[]>(['warehouse-items'], getAllWarehouseItems, options);

export const useWarehouseItemCategories = (
  options = getDefaultOptions<WarehouseItemCategoryDto[]>()
): UseQueryResult<WarehouseItemCategoryDto[], unknown> =>
  useQuery<WarehouseItemCategoryDto[]>(['warehouse-item-categories'], getAllWarehouseItemCategories, options);

export const useAllProductCategories = (
  options = getDefaultOptions<ProductCategoryDetailsDto[]>()
): UseQueryResult<ProductCategoryDetailsDto[], unknown> => {
  return useQuery<ProductCategoryDetailsDto[]>(['product-categories'], getAllProductCategories, options);
};

export const allProductMenusCacheKey = 'product-menus';
export const useAllProductMenus = (
  options = getDefaultOptions<ProductMenuDto[]>()
): UseQueryResult<ProductMenuDto[], unknown> => {
  return useQuery<ProductMenuDto[]>([allProductMenusCacheKey], getAllProductMenus, options);
};

export const defaultMenuCacheKey = 'default-menu';
export const useDefaultProductMenu = (
  options = getDefaultOptions<ProductMenuDto>()
): UseQueryResult<ProductMenuDto, unknown> => {
  return useQuery<ProductMenuDto>([defaultMenuCacheKey], getDefaultProductMenu, options);
};

export const conciseMenuCacheKey = 'concise-menu';
export const useConciseProductMenu = (
  options = getDefaultOptions<ProductMenuDto>()
): UseQueryResult<ProductMenuDto, unknown> => {
  return useQuery<ProductMenuDto>([conciseMenuCacheKey], getConciseProductMenu, options);
};

export const foodoraMenuCacheKey = 'foodora-menu';
export const useFoodoraProductMenu = (
  options = getDefaultOptions<ProductMenuDto>()
): UseQueryResult<ProductMenuDto, unknown> => {
  return useQuery<ProductMenuDto>([foodoraMenuCacheKey], getFoodoraProductMenu, options);
};

export const useAllProductMenusForShop = (
  shopExternalId: string,
  options = getDefaultOptions<ShopProductMenuDto[]>()
): UseQueryResult<ShopProductMenuDto[], unknown> => {
  return useQuery<ShopProductMenuDto[]>([shopExternalId], () => getProductMenusForShop(shopExternalId), options);
};

export const allOutOfStockProductsCacheKey = 'out-of-stock-products';
export const useAllOutOfStockProducts = (): UseQueryResult<OutOfStockProductDto[], unknown> => {
  return useQuery([allOutOfStockProductsCacheKey], getAllOutOfStockProducts);
};

export const allOutOfStockIngredientsCacheKey = 'out-of-stock-ingredients';
export const useAllOutOfStockIngredients = (): UseQueryResult<OutOfStockIngredientDto[], unknown> => {
  return useQuery([allOutOfStockIngredientsCacheKey], getAllOutOfStockIngredients);
};

export const allOutOfStockItemsCacheKey = 'out-of-stock-items';
export const useAllOutOfStockItemsByShopExternalId = (
  shopExternalId: string,
  refetchOnMount: boolean | 'always' = 'always'
): UseQueryResult<GetAllOutOfStockItemsResponse> => {
  return useQuery(
    [allOutOfStockItemsCacheKey, shopExternalId],
    () => getAllOutOfStockItemsByShopExternalId(shopExternalId),
    {
      enabled: !!shopExternalId,
      refetchOnMount,
    }
  );
};

export const allVatRatesCacheKey = 'vat-rates';
export const useAllVatRates = (): UseQueryResult<VatDto[], unknown> => {
  return useQuery([allVatRatesCacheKey], getAllVatRates);
};

const getAllDisplayGroups = (): Promise<DisplayGroupDto[]> => {
  const client = getApiClient();
  return client.getAllDisplayGroups();
};

export const allDisplayGroupsCacheKey = 'display-groups';
export const useAllDisplayGroups = (): UseQueryResult<DisplayGroupDto[]> => {
  return useQuery([allDisplayGroupsCacheKey], getAllDisplayGroups);
};

export const allTagsCacheKey = 'product-tags';
export const useAllTags = (): UseQueryResult<TagDto[], unknown> => {
  return useQuery([allTagsCacheKey], getAllTags);
};

const saveProductsDisplayOrder = (request: UpdateProductDisplayOrderDto[]) => {
  const client = getApiClient();
  return client.updateProductDisplayOrders(request);
};

export const useSaveProductsDisplayOrder = (
  ui?: ApiMutationUiOptions<UpdateProductDisplayOrderDto[], UpdateProductDisplayOrderDto[]>
) => useProductsMutation(saveProductsDisplayOrder, { invalidateKeys: [allProductsCacheKey], ...ui });

export const importFoodoraCatalogToShops = (request: ImportFoodoraCatalogToShopsRequest) => {
  const client = getApiClient();
  return client.importFoodoraCatalogToShops(request);
};

export const useUpdateOutOfStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOutOfStockItem,
    onSuccess: () => {
      queryClient.invalidateQueries(['out-of-stock-items']);
    },
  });
};

export const useRemoveOutOfStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeOutOfStock,
    onSuccess: () => {
      queryClient.invalidateQueries(['out-of-stock-items']);
    },
  });
};

const useProductsMutation = <TData = unknown, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  options: Omit<ApiMutationOptions<TData, TVariables>, 'getError'>
) => useApiMutation(mutationFn, { getError: getProductsApiError, ...options });

export const useRestoreOutOfStockItem = () =>
  useProductsMutation(removeOutOfStock, {
    invalidateKeys: [[allOutOfStockProductsCacheKey], [allOutOfStockIngredientsCacheKey], [allOutOfStockItemsCacheKey]],
    successMessage: t('global.saveSucceeded'),
  });
